import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, 
  Send, 
  Edit, 
  Trash2, 
  Eye, 
  Users,
  Calendar,
  Play,
  Pause,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { createSafeHTML } from '@/utils/sanitization';
import { glassCard, glassCardStatic } from '@/utils/admin-glass-styles';

interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  content_html: string;
  content_text: string | null;
  recipient_type: string;
  status: string;
  scheduled_for: string | null;
  sent_at: string | null;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content_html: string;
  content_text: string | null;
}

const AdminEmailCampaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<EmailCampaign | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content_html: '',
    content_text: '',
    recipient_type: 'all_users',
    template_id: '',
    scheduled_for: ''
  });

  const [recipientStats, setRecipientStats] = useState({
    total_clients: 0,
    total_practitioners: 0,
    total_users: 0
  });

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchRecipientStats();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, name, subject, content_html, content_text')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchRecipientStats = async () => {
    try {
      const [clientsResult, practitionersResult] = await Promise.all([
        supabase.from('clients').select('id', { count: 'exact', head: true }),
        supabase.from('practitioners').select('id', { count: 'exact', head: true })
      ]);

      const total_clients = clientsResult.count || 0;
      const total_practitioners = practitionersResult.count || 0;
      const total_users = total_clients + total_practitioners;

      setRecipientStats({
        total_clients,
        total_practitioners,
        total_users
      });
    } catch (error) {
      console.error('Error fetching recipient stats:', error);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const recipientCount = getRecipientCount(formData.recipient_type);
      
      const { data, error } = await supabase
        .from('email_campaigns')
        .insert([{
          ...formData,
          total_recipients: recipientCount,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          scheduled_for: formData.scheduled_for || null
        }])
        .select()
        .single();

      if (error) throw error;

      setCampaigns([data, ...campaigns]);
      setIsCreateModalOpen(false);
      resetForm();
      toast({
        title: "Success",
        description: "Email campaign created successfully"
      });
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast({
        title: "Error",
        description: "Failed to create email campaign",
        variant: "destructive"
      });
    }
  };

  const handleSendCampaign = async (campaignId: string) => {
    if (!confirm('Are you sure you want to send this campaign?')) return;

    try {
      // Call edge function to send campaign
      const { error } = await supabase.functions.invoke('send-campaign', {
        body: { campaign_id: campaignId }
      });

      if (error) throw error;

      // Update campaign status
      const { error: updateError } = await supabase
        .from('email_campaigns')
        .update({ status: 'sending' })
        .eq('id', campaignId);

      if (updateError) throw updateError;

      fetchCampaigns();
      toast({
        title: "Success",
        description: "Campaign sending started"
      });
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast({
        title: "Error",
        description: "Failed to send campaign",
        variant: "destructive"
      });
    }
  };

  const handleScheduleCampaign = async (campaignId: string, scheduledFor: string) => {
    try {
      const { error } = await supabase
        .from('email_campaigns')
        .update({ 
          status: 'scheduled',
          scheduled_for: scheduledFor
        })
        .eq('id', campaignId);

      if (error) throw error;

      fetchCampaigns();
      toast({
        title: "Success",
        description: "Campaign scheduled successfully"
      });
    } catch (error) {
      console.error('Error scheduling campaign:', error);
      toast({
        title: "Error",
        description: "Failed to schedule campaign",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;

    try {
      const { error } = await supabase
        .from('email_campaigns')
        .delete()
        .eq('id', campaignId);

      if (error) throw error;

      setCampaigns(campaigns.filter(c => c.id !== campaignId));
      toast({
        title: "Success",
        description: "Campaign deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast({
        title: "Error",
        description: "Failed to delete campaign",
        variant: "destructive"
      });
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        ...formData,
        template_id: templateId,
        subject: template.subject,
        content_html: template.content_html,
        content_text: template.content_text || ''
      });
    }
  };

  const getRecipientCount = (recipientType: string) => {
    switch (recipientType) {
      case 'all_clients':
        return recipientStats.total_clients;
      case 'all_practitioners':
        return recipientStats.total_practitioners;
      case 'all_users':
        return recipientStats.total_users;
      default:
        return 0;
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      content_html: '',
      content_text: '',
      recipient_type: 'all_users',
      template_id: '',
      scheduled_for: ''
    });
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      sending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    
    return (
      <Badge className={variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'sending':
        return <Play className="h-4 w-4 text-yellow-500" />;
      case 'scheduled':
        return <Clock className="h-4 w-4 text-blue-500" />;
      default:
        return <Pause className="h-4 w-4 text-gray-500" />;
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Email Campaigns</h2>
          <p className="text-muted-foreground">
            Create and manage bulk email campaigns
          </p>
        </div>
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Create Email Campaign</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Campaign Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter campaign name"
                  />
                </div>
                <div>
                  <Label htmlFor="template">Use Template (Optional)</Label>
                  <Select value={formData.template_id} onValueChange={handleTemplateSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">No template</SelectItem>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="recipient_type">Recipients</Label>
                  <Select value={formData.recipient_type} onValueChange={(value) => setFormData({...formData, recipient_type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_users">All Users ({recipientStats.total_users})</SelectItem>
                      <SelectItem value="all_clients">All Clients ({recipientStats.total_clients})</SelectItem>
                      <SelectItem value="all_practitioners">All Practitioners ({recipientStats.total_practitioners})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="scheduled_for">Schedule For (Optional)</Label>
                  <Input
                    id="scheduled_for"
                    type="datetime-local"
                    value={formData.scheduled_for}
                    onChange={(e) => setFormData({...formData, scheduled_for: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="subject">Subject Line</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="Enter email subject"
                />
              </div>
              <div>
                <Label htmlFor="content_html">HTML Content</Label>
                <Textarea
                  id="content_html"
                  value={formData.content_html}
                  onChange={(e) => setFormData({...formData, content_html: e.target.value})}
                  placeholder="Enter HTML content"
                  rows={8}
                />
              </div>
              <div>
                <Label htmlFor="content_text">Plain Text Content (Optional)</Label>
                <Textarea
                  id="content_text"
                  value={formData.content_text}
                  onChange={(e) => setFormData({...formData, content_text: e.target.value})}
                  placeholder="Enter plain text content"
                  rows={4}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateCampaign} className="flex-1">
                  Create Campaign
                </Button>
                <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className={glassCard}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Recipients</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{recipientStats.total_users.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {recipientStats.total_clients} clients, {recipientStats.total_practitioners} practitioners
            </p>
          </CardContent>
        </Card>
        <Card className={glassCard}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Campaigns</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {campaigns.filter(c => c.status === 'sending' || c.status === 'scheduled').length}
            </div>
            <p className="text-xs text-muted-foreground">
              Currently running or scheduled
            </p>
          </CardContent>
        </Card>
        <Card className={glassCard}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sent This Month</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {campaigns.filter(c => c.status === 'sent').reduce((sum, c) => sum + c.sent_count, 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Total emails delivered
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="sending">Sending</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Campaigns ({filteredCampaigns.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCampaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(campaign.status)}
                        {getStatusBadge(campaign.status)}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{campaign.name}</TableCell>
                    <TableCell className="max-w-xs truncate" title={campaign.subject}>
                      {campaign.subject}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{campaign.recipient_type.replace('_', ' ')}</Badge>
                      <div className="text-sm text-muted-foreground">
                        {campaign.total_recipients} recipients
                      </div>
                    </TableCell>
                    <TableCell>
                      {campaign.status === 'sent' && (
                        <div className="text-sm">
                          <div className="text-green-600">{campaign.sent_count} sent</div>
                          {campaign.failed_count > 0 && (
                            <div className="text-red-600">{campaign.failed_count} failed</div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(campaign.created_at), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedCampaign(campaign);
                            setIsPreviewModalOpen(true);
                          }}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        {campaign.status === 'draft' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSendCampaign(campaign.id)}
                          >
                            <Send className="h-3 w-3" />
                          </Button>
                        )}
                        {campaign.status !== 'sent' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteCampaign(campaign.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={isPreviewModalOpen} onOpenChange={setIsPreviewModalOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Campaign Preview: {selectedCampaign?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Subject:</Label>
                <p className="font-medium">{selectedCampaign?.subject}</p>
              </div>
              <div>
                <Label>Recipients:</Label>
                <p className="font-medium">{selectedCampaign?.total_recipients} users</p>
              </div>
            </div>
            <div>
              <Label>HTML Content:</Label>
              <div 
                className="border rounded-lg p-4 max-h-96 overflow-auto"
                dangerouslySetInnerHTML={createSafeHTML(selectedCampaign?.content_html || '')}
              />
            </div>
            {selectedCampaign?.content_text && (
              <div>
                <Label>Plain Text Content:</Label>
                <pre className="border rounded-lg p-4 max-h-48 overflow-auto whitespace-pre-wrap text-sm">
                  {selectedCampaign.content_text}
                </pre>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmailCampaigns;